import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  Title,
  Flex,
  Text,
  Button,
  MultiSelect,
  MultiSelectItem,
  Select,
  SelectItem,
  Badge,
} from "@tremor/react";
import {
  ArrowPathIcon,
  ArrowUpOnSquareIcon,
} from "@heroicons/react/24/outline";
import Spinner from "../../../components/spinner";
import UplinkTimeline from "./logtable";
import * as devicesAPI from "../../../services/device";
import Pagination from "./pagination";
import moment from "moment";
import DateRange from "../../../components/DateRange";
import ReactGA from "react-ga4";
import { GA_EVENTS } from "../../../services/ga-events";
import { initAnalytics } from "../../../services/analytics";
initAnalytics();

export default function LogPanel({ deviceId, capabilities }) {
  const [events, setEvents] = useState({ readings: [] });
  const [pakcetCount, setPacketCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [timeFrame, setTimeFrame] = useState("24h");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const isFirstRender = useRef(true);
  const [limit, setLimit] = useState(10);

  // get filter values
  const [channels, setChannels] = useState([]);
  const [types, setTypes] = useState([]);
  const [units, setUnits] = useState([]);

  // set filter values
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);

  const getStartDate = () => {
    let startDate;
    switch (timeFrame) {
      case "15m":
        startDate = moment().subtract(15, "minutes").toISOString();
        break;
      case "1h":
        startDate = moment().subtract(1, "hours").toISOString();
        break;
      case "30m":
        startDate = moment().subtract(30, "minutes").toISOString();
        break;
      case "24h":
        startDate = moment().subtract(24, "hours").toISOString();
        break;
      case "3d":
        startDate = moment().subtract(3, "days").toISOString();
        break;
      case "1w":
        startDate = moment().subtract(1, "weeks").toISOString();
        break;
      case "1mo":
        startDate = moment().subtract(1, "months").toISOString();
        break;
      default:
        startDate = moment().subtract(15, "minutes").toISOString();
    }

    return startDate;
  };

  // The API paginates per point; ask for enough points to fill `limit` uplinks.
  const channelCount =
    selectedChannels.length ||
    new Set(
      capabilities
        .filter(
          (cap) =>
            cap.type !== "digital_actuator" && cap.type !== "analog_actuator",
        )
        .map((cap) => cap.channel),
    ).size;
  const pointLimit = limit * Math.max(channelCount, 1);

  const getReadings = async () => {
    try {
      setLoading(true);
      const filters = {
        channels: selectedChannels,
        types: selectedTypes,
        units: selectedUnits,
      };
      let data;
      if (timeFrame === "-1") {
        data = await devicesAPI.fetchReadings(
          deviceId,
          currentPage,
          pointLimit,
          null,
          null,
          filters,
        );
      } else {
        const endDate = moment().toISOString();
        const startDate = getStartDate();
        data = await devicesAPI.fetchReadings(
          deviceId,
          currentPage,
          pointLimit,
          startDate,
          endDate,
          filters,
        );
      }
      setEvents(data);
      setTotalPages(data.totalPages);
      setPacketCount(data.count);
      setLoading(false);
      return data;
    } catch (err) {
      console.error("Error fetching device:", err);
    }
  };

  useEffect(() => {
    ReactGA.event({
      category: "Logs Console",
      action: GA_EVENTS.LOGS_VIEW,
    });
    if (deviceId === undefined) {
      return;
    }
    if (isFirstRender.current) {
      isFirstRender.current = false;
      getReadings();
    } else {
      getReadings();
    }
  }, [
    deviceId,
    currentPage,
    selectedChannels,
    selectedTypes,
    selectedUnits,
    timeFrame,
    pointLimit,
  ]);

  useEffect(() => {
    // map capabilities to filter values
    const filteredCapabilities = capabilities.filter((cap) => {
      return cap.type !== "digital_actuator" && cap.type !== "analog_actuator";
    });

    const channels = filteredCapabilities.map((cap) => cap.channel);
    const types = filteredCapabilities
      .filter((cap) => cap.type)
      .map((cap) => cap.type);
    const units = filteredCapabilities
      .filter((cap) => cap.unit)
      .map((cap) => cap.unit);

    setChannels([...new Set(channels)]);
    setTypes([...new Set(types)]);
    setUnits([...new Set(units)]);
  }, [capabilities]);

  const onChange = (page) => {
    setCurrentPage(page);
    getReadings();
  };

  const onDateSelector = async (value) => {
    setTimeFrame(value);
    await getReadings();
  };

  const onRefresh = () => {
    getReadings();
  };

  const onChannelChange = (value) => {
    // set selected channels currently with react state
    setSelectedChannels([...value]);
  };

  const convertJSONToCSV = (jsonData) => {
    const readings = jsonData.readings;
    const keys = Object.keys(readings[0].metadata);
    const csvRows = readings.map((reading) => {
      const metadataValues = keys.map((key) => reading.metadata[key]);
      const v =
        reading.metadata.type === "gps" ? reading.array_value : reading.value;
      return [reading.timestamp, ...metadataValues].join(",") + "," + v;
    });
    return ["timestamp," + keys.join(",") + ",value", ...csvRows].join("\n");
  };

  const downloadCSV = (csvData, filename) => {
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", filename);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const onExport = async () => {
    try {
      setExporting(true);

      // Calculate date range based on timeFrame selection
      let startDate, endDate;
      endDate = moment().toISOString();

      if (timeFrame === "-1") {
        alert("Please select a specific time frame for export (not 'All time'). Maximum export range is 1 day.");
        setExporting(false);
        return;
      }

      startDate = getStartDate();

      // Validate that the range is not more than 1 day
      const startMoment = moment(startDate);
      const endMoment = moment(endDate);
      const diffInDays = endMoment.diff(startMoment, 'days', true);

      if (diffInDays > 1) {
        alert("Export range cannot exceed 1 day. Please select a smaller time frame.");
        setExporting(false);
        return;
      }

      // Build filters
      const filters = {
        channels: selectedChannels,
        types: selectedTypes,
        units: selectedUnits,
      };

      // Fetch all data within the range (no pagination for export)
      const exportData = await devicesAPI.fetchReadings(
        deviceId,
        1,  // page 1
        10000,  // large limit to get all data within 1 day range
        startDate,
        endDate,
        filters
      );

      if (!exportData.readings || exportData.readings.length === 0) {
        alert("No data to export in the selected time range.");
        setExporting(false);
        return;
      }

      const csvData = convertJSONToCSV(exportData);
      const dateStr = moment().format('YYYY-MM-DD-HHmmss');
      downloadCSV(csvData, `zafron-logs-export-${dateStr}.csv`);

      // Track the export event
      ReactGA.event({
        category: "Logs Console",
        action: "CSV_EXPORT",
        label: `Range: ${timeFrame}, Records: ${exportData.readings.length}`
      });
    } catch (err) {
      console.error("Error exporting data:", err);
      alert("Failed to export data. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <Card className="mt-5">
        <Flex>
          <div>
            <Flex justifyContent="start" className="space-x-2">
              <Title>Logs</Title>
              <Badge size="xs">{pakcetCount} Readings</Badge>
            </Flex>
          </div>
          <div>
            <Flex justifyContent="end" className="space-x-2"></Flex>
          </div>
        </Flex>

        {/* <Histogram /> */}
        <hr className="mt-2" />
        <div className="filter-export">
          <Flex className="space-x-2 mt-2 flex-wrap md:flex-nowrap">
            <Text className="w-32">Filter:</Text>
            <MultiSelect placeholder="Channel" onValueChange={onChannelChange}>
              {channels.map((channel) => (
                <MultiSelectItem key={channel} value={channel}>
                  {channel}
                </MultiSelectItem>
              ))}
            </MultiSelect>
            <MultiSelect placeholder="Type" onValueChange={setSelectedTypes}>
              {types.map((type) => (
                <MultiSelectItem key={type} value={type}>
                  {type}
                </MultiSelectItem>
              ))}
            </MultiSelect>
            <MultiSelect placeholder="Unit" onValueChange={setSelectedUnits}>
              {units.map((unit) => (
                <MultiSelectItem key={unit} value={unit}>
                  {unit}
                </MultiSelectItem>
              ))}
            </MultiSelect>
            <DateRange onDateSelector={onDateSelector} />
            <Button
              onClick={onExport}
              icon={ArrowUpOnSquareIcon}
              tooltip="CSV Export (Max 1 day range)"
              disabled={exporting}
              loading={exporting}
            >{exporting ? "Exporting..." : ""}</Button>
            <Button
              onClick={onRefresh}
              tooltip="Refresh"
              variant="secondary"
              icon={ArrowPathIcon}
            ></Button>
          </Flex>
          <style jsx="true">{`
            @media (max-width: 768px) {
              .flex-wrap {
                flex-direction: column;
                align-items: flex-start;
              }
              .space-x-2 > * + * {
                margin-top: 8px;
                margin-left: 0;
              }
            }
          `}</style>
        </div>
        {loading ? (
          <Spinner />
        ) : (
          <div className="logtable">
            <UplinkTimeline logs={events} />
            {events.readings.length > 1 && (
              <div className="mt-4">
                {/* show limit dropdown */}
                <Flex justifyContent="between">
                  <div className="mr-4 flex items-center">
                    <Text className="mr-2">Show:</Text>
                    <Select
                      placeholder="Limit"
                      onValueChange={setLimit}
                      defaultValue={limit}
                    >
                      <SelectItem value={10}>10 uplinks</SelectItem>
                      <SelectItem value={25}>25 uplinks</SelectItem>
                      <SelectItem value={50}>50 uplinks</SelectItem>
                    </Select>
                  </div>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onChange={onChange}
                  />
                </Flex>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
